/**
 * ⌘K 搜索的「内容」部分：配置里的一行一行——某一关、某栋建筑、某张卡、某件商品、某个 idol。
 *
 * 两个来源：
 * - 版本快照：App 已经拿在手里（关卡、建筑、奖励、卡包、spin、宠物、头像框……），打开搜索时现算，不发请求
 * - 活表：不进版本、各页自己拉的（商城、物品资料、内购、idol、任务成就、活动道具、存钱罐、分享链接），
 *   打开搜索时并发拉一次，按代理缓存一分钟；哪个拉不到就少那一段，不挡别的
 *
 * 纯函数（拉取函数由调用方传入），node --test 直接测。名字的各语言版本都进 alt：运营切到英文界面，
 * 记得的往往还是中文名。
 */
import type { SearchDoc } from "./nav-search";

type I18n = Record<string, string | undefined> | undefined | null;

/** 一个名字的所有写法：本名 + 各语言 + 编号，去空去重 */
export function names(...vals: Array<string | number | I18n | Array<string | undefined> | null | undefined>): string[] {
  const out: string[] = [];
  const add = (v: unknown) => {
    if (v == null) return;
    if (typeof v === "string" || typeof v === "number") { const s = String(v).trim(); if (s && !out.includes(s)) out.push(s); return; }
    if (Array.isArray(v)) { v.forEach(add); return; }
    if (typeof v === "object") Object.values(v as Record<string, unknown>).forEach(add);
  };
  vals.forEach(add);
  return out;
}

/** 一行内容 → 搜索项。title 取第一个非空名字，其余名字与编号进 alt */
function row(o: { tag: string; page: string; key: string; path: string; all: string[]; rowKey?: string | number; match?: string[]; go?: () => void }): SearchDoc | null {
  const [title, ...rest] = o.all;
  if (!title) return null;
  return {
    kind: "content", tag: o.tag, page: o.page, key: `${o.page}:${o.key}`, title, path: o.path, alt: rest,
    rowKey: o.rowKey == null ? undefined : String(o.rowKey), match: o.match ?? o.all, go: o.go,
  };
}

/** 版本快照里的内容。字段只取搜索要用的那几个，类型故意写宽：各页的模型类型在这里只读不改 */
export interface SnapshotContent {
  levels?: Array<{ id: number; name: string; stage?: string; i18n?: Record<string, { name?: string; stage?: string }>; buildings?: Array<{ code: string; name: string }> }>;
  rewards?: Array<{ id: string; levelId: number; name: string; i18n?: I18n }>;
  itemPacks?: Pack[];
  idolPacks?: Pack[];
  spins?: Array<{ id: string; code: number | string; name: string; i18n?: I18n }>;
  pets?: Array<{ localKey?: string; petId?: string; name: string; nameI18n?: I18n; species?: string; speciesI18n?: I18n }>;
  frames?: Array<{ frameId: string; name: string; nameI18n?: I18n }>;
  battleStages?: Array<{ stage: number | string; name: string; nameI18n?: I18n }>;
  abilityCards?: Array<{ cardId: string; name: string; nameI18n?: I18n }>;
  socialChannels?: Array<{ code: string; name: string; nameI18n?: I18n }>;
  talentNodes?: Array<{ nodeId: string; nameI18n?: I18n }>;
  pettownSlots?: Array<{ slot: number | string; nameI18n?: I18n }>;
  /** 建筑体系：天赋树在哪一页取决于它 */
  buildingSystem?: string;
}
type Pack = { id: string; code?: string; name: string; i18n?: I18n; cards?: Array<{ code: string; name: string; i18n?: I18n }> };

/** 实体名：结果行左边的标签。由调用方按界面语言给 */
export type ContentTags = Record<
  "level" | "building" | "reward" | "pack" | "card" | "spin" | "pet" | "frame" | "stage" | "ability" | "channel" | "talent" | "slot"
  | "shopItem" | "shopSection" | "assetInfo" | "iap" | "idol" | "task" | "achievement" | "title" | "actItem" | "actEvent" | "piggy" | "shareLink",
  string
>;

export function snapshotDocs(s: SnapshotContent, tags: ContentTags, path: (page: string) => string, selectLevel?: (id: number) => void): SearchDoc[] {
  const out: (SearchDoc | null)[] = [];
  for (const l of s.levels ?? []) {
    const ln = names(l.name, l.stage, ...Object.values(l.i18n ?? {}).map((x) => [x.name, x.stage]));
    // 关卡号也能搜：「第 3 关」「3」
    out.push(row({ tag: tags.level, page: "level", key: String(l.id), path: path("level"), all: [...(ln.length ? ln : [`#${l.id}`]), `第${l.id}关`, `#${l.id}`], rowKey: l.id }));
    for (const b of l.buildings ?? []) {
      out.push(row({
        tag: tags.building, page: "building", key: `${l.id}:${b.code}`, path: `${path("building")} / ${ln[0] ?? `#${l.id}`}`,
        all: names(b.name, b.code), match: names(b.name, b.code), go: selectLevel ? () => selectLevel(l.id) : undefined,
      }));
    }
  }
  for (const r of s.rewards ?? []) out.push(row({ tag: tags.reward, page: "reward", key: r.id, path: path("reward"), all: names(r.name, r.i18n) }));
  for (const [page, packs] of [["card-item", s.itemPacks], ["card-idol", s.idolPacks]] as const) {
    for (const p of packs ?? []) {
      const pn = names(p.name, p.i18n, p.code);
      out.push(row({ tag: tags.pack, page, key: p.id, path: path(page), all: pn, rowKey: p.id }));
      // 卡在卡包抽屉里，页上只有卡包那一行：定位到卡包
      for (const c of p.cards ?? []) {
        out.push(row({ tag: tags.card, page, key: `${p.id}:${c.code}`, path: `${path(page)} / ${pn[0] ?? p.id}`, all: names(c.name, c.i18n, c.code), rowKey: p.id, match: pn }));
      }
    }
  }
  for (const c of s.spins ?? []) out.push(row({ tag: tags.spin, page: "spin", key: c.id, path: path("spin"), all: names(c.name, c.i18n, `code ${c.code}`) }));
  for (const p of s.pets ?? []) out.push(row({ tag: tags.pet, page: "pet-list", key: p.localKey ?? p.petId ?? p.name, path: path("pet-list"), all: names(p.name, p.nameI18n, p.petId, p.species, p.speciesI18n) }));
  for (const f of s.frames ?? []) out.push(row({ tag: tags.frame, page: "frame", key: f.frameId, path: path("frame"), all: names(f.name, f.nameI18n, f.frameId) }));
  for (const b of s.battleStages ?? []) out.push(row({ tag: tags.stage, page: "battle-stage", key: String(b.stage), path: path("battle-stage"), all: names(b.name, b.nameI18n) }));
  for (const a of s.abilityCards ?? []) out.push(row({ tag: tags.ability, page: "ability-card", key: a.cardId, path: path("ability-card"), all: names(a.name, a.nameI18n, a.cardId) }));
  for (const c of s.socialChannels ?? []) out.push(row({ tag: tags.channel, page: "social-channel", key: c.code, path: path("social-channel"), all: names(c.name, c.nameI18n, c.code) }));
  const talentPage = s.buildingSystem === "pettown" ? "pettown-talent" : "level-talent";
  for (const n of s.talentNodes ?? []) out.push(row({ tag: tags.talent, page: talentPage, key: n.nodeId, path: path(talentPage), all: names(n.nameI18n, n.nodeId) }));
  for (const sl of s.pettownSlots ?? []) out.push(row({ tag: tags.slot, page: "pettown-slot", key: String(sl.slot), path: path("pettown-slot"), all: names(sl.nameI18n, sl.slot) }));
  return out.filter((d): d is SearchDoc => !!d);
}

/** 活表：各页自己拉的列表。字段同样只取搜索要用的 */
export interface LiveContent {
  shopItems?: Array<{ itemId: string; name: string; nameI18n?: I18n }>;
  shopSections?: Array<{ sectionId: string; name: string; nameI18n?: I18n }>;
  assetInfo?: Array<{ assetType: string; assetId: string; nameI18n?: I18n }>;
  iapItems?: Array<{ itemId: string; name: string; nameI18n?: I18n }>;
  idols?: Array<{ idolId: string; stageName: string; stageNameI18n?: I18n; realName?: string }>;
  achievements?: Array<{ achievementId: string; kind: string; title: string; titleI18n?: I18n }>;
  titles?: Array<{ titleId: string; name: string; nameI18n?: I18n }>;
  /** 二合那套任务成就（同形，页 id 带 erhe- 前缀） */
  erheAchievements?: Array<{ achievementId: string; kind: string; title: string; titleI18n?: I18n }>;
  erheTitles?: Array<{ titleId: string; name: string; nameI18n?: I18n }>;
  actItems?: Array<{ ref: string; name: string; nameI18n?: I18n }>;
  actEvents?: Array<{ eventId: string; nameI18n?: I18n }>;
  piggies?: Array<{ piggyId: string; nameI18n?: I18n }>;
  shareLinks?: Array<{ link_id: string; title: string; title_i18n?: I18n }>;
}

export function liveDocs(s: LiveContent, tags: ContentTags, path: (page: string) => string): SearchDoc[] {
  const out: (SearchDoc | null)[] = [];
  const add = (tag: string, page: string, key: string, all: string[]) => out.push(row({ tag, page, key, path: path(page), all }));
  for (const x of s.shopItems ?? []) add(tags.shopItem, "shop", x.itemId, names(x.name, x.nameI18n, x.itemId));
  for (const x of s.shopSections ?? []) add(tags.shopSection, "shop-sections", x.sectionId, names(x.name, x.nameI18n, x.sectionId));
  for (const x of s.assetInfo ?? []) add(tags.assetInfo, "asset-info", `${x.assetType}/${x.assetId}`, names(x.nameI18n, x.assetId));
  for (const x of s.iapItems ?? []) add(tags.iap, "iap", x.itemId, names(x.name, x.nameI18n, x.itemId));
  for (const x of s.idols ?? []) add(tags.idol, "idol-list", x.idolId, names(x.stageName, x.stageNameI18n, x.realName, x.idolId));
  // 任务（每日 / 每周 / 新手）在任务管理页，其余在成就管理页
  // 两套任务成就（Idol Ludo / 二合）各自的页：二合的页 id 是 erhe-achievement-*
  for (const [list, titles, prefix] of [[s.achievements, s.titles, ""], [s.erheAchievements, s.erheTitles, "erhe-"]] as const) {
    for (const x of list ?? []) add(x.kind === "task" ? tags.task : tags.achievement, `${prefix}${x.kind === "task" ? "achievement-tasks" : "achievement-achievements"}`, x.achievementId, names(x.title, x.titleI18n, x.achievementId));
    for (const x of titles ?? []) add(tags.title, `${prefix}achievement-rewards`, x.titleId, names(x.name, x.nameI18n, x.titleId));
  }
  for (const x of s.actItems ?? []) add(tags.actItem, "act-item", x.ref, names(x.name, x.nameI18n, x.ref));
  for (const x of s.actEvents ?? []) add(tags.actEvent, "act-item", x.eventId, names(x.nameI18n, x.eventId));
  for (const x of s.piggies ?? []) add(tags.piggy, "piggy", x.piggyId, names(x.nameI18n, x.piggyId));
  for (const x of s.shareLinks ?? []) add(tags.shareLink, "share-links", x.link_id, names(x.title, x.title_i18n));
  return out.filter((d): d is SearchDoc => !!d);
}

/**
 * 到了页上找那一行：DataTable 的行标识（data-lens-row）优先，其次是文字或输入框的值等于名字的表格行。
 * 返回 null 表示这一页上没有（在弹窗里、虚拟滚动没渲染到、或页面还没画完）。
 */
export function findRow(root: ParentNode, doc: Pick<SearchDoc, "rowKey" | "match">): HTMLElement | null {
  if (doc.rowKey != null) {
    const byKey = root.querySelector<HTMLElement>(`tr[data-lens-row=${JSON.stringify(doc.rowKey)}]`);
    if (byKey) return byKey;
  }
  // 按 match 的顺序找：名字排在编号前面，先按名字认，认不出再按编号
  const rows = Array.from(root.querySelectorAll<HTMLElement>("tbody tr"));
  for (const want of (doc.match ?? []).map((s) => s.trim()).filter(Boolean)) {
    for (const tr of rows) {
      for (const cell of Array.from(tr.querySelectorAll<HTMLElement>("td"))) {
        if ((cell.textContent ?? "").trim() === want) return tr;
        for (const inp of Array.from(cell.querySelectorAll<HTMLInputElement>("input"))) if (inp.value.trim() === want) return tr;
      }
    }
  }
  return null;
}
